import React, { useState, useEffect } from 'react';
import { Box, Text, Stack, Meter, Image, Collapsible, Button, Spinner } from 'grommet';
import { Close, Down } from 'grommet-icons';
import { qlikBrand } from '../../branding';
import ActionableNotice from '../../components/ActionableNotice';
import MetricBox from '../../components/MetricBox';
import busImage from '../../school-bus_256.png';
import BusBreakdown from '../../components/BusBreakdown';
import Notes from '../Notes'
import clone from 'lodash/clone';
import moment from 'moment';
import { formatDistanceToNowStrict } from 'date-fns';
import { API } from '../../libs/amplifyAPI';


const BusOperations = () => {
  const [nextBusOpen, setNextBusOpen] = useState(false)
  const [recentProjects, setRecentProjects] = useState({ open: [] })
  const [notesLayerOpen, setNotesLayerOpen] = useState(undefined)
  const [creatingNote, setCreatingNote] = useState(false)
  
  // State moved from Main
  const [nextBusCountdown, setNextBusCountdown] = useState(<Spinner />)
  const [modifications, setModifications] = useState({
    strings: <Spinner />,
    repositories: <Spinner />,
    files: <Spinner />,
    words: <Spinner />
  })
  const [activeProjects, setActiveProjects] = useState(<Spinner />)
  const [projects, setProjects] = useState([])
  const [repositories, setRepositories] = useState({ repositories: [] })
  const [notes, setNotes] = useState(<Spinner />)
  const [nextBusDate, setNextBusDate] = useState(predicNextBusDate())
  const [interval, setIntervalId] = useState(null)

  function predicNextBusDate() {
    // Predicts Tuesday and Friday buses
    let date = moment();
    let day = date.day();
    let daysUntilNext;
    if (day === 0 || day === 1 || day === 2) {
        daysUntilNext = 2 - day;
    } else if (day === 3 || day === 4) {
        daysUntilNext = 5 - day;
    } else {
        daysUntilNext = (9 - day) % 7;
    }
    date.add(daysUntilNext, 'days');
    return date.format();
  }

  useEffect(() => {
    API.get('NextUIBus', 'ui-bus/next-bus')
    .then(data => {
      setNextBusDate(data.nextBusDate)
      setModifications(data.modifications)
      
      
      setRepositories(data.repositories || { repositories: [] })
      
      const nextbusDate = moment(data.nextBusDate).toDate()
      const newNextBusCountdown = formatDistanceToNowStrict(nextbusDate)
      setNextBusCountdown(newNextBusCountdown)
      
      const intervalId = setInterval(() => {
        setNextBusCountdown(formatDistanceToNowStrict(moment(data.nextBusDate).toDate()))
      }, 60000)
      setIntervalId(intervalId)
      
      API.get('Notes', 'notes', { queryStringParameters: { projectDate: moment(data.nextBusDate).utc().format('YYYY-MM-DD') }})
      .then(notesData => {
        setNotes(notesData.notes)
      })
      .catch(err => {
        console.log(err)
      })
    })
    .catch(err => {
      console.log(err)
    })

    API.get('LatestUIProjects', 'ui-bus/latest-projects')
    .then(data => {
      // setRecentProjects(data.projects || [])
      setProjects(data.projects)
      setActiveProjects(data.activeProjects)
    })
    .catch(err => {
      console.log(err)
    })

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onNotesLayerOpen = () => setNotesLayerOpen(true)

  const onNotesLayerClose = () => setNotesLayerOpen(undefined)

  const renderRecentProject = ({ project, index }) => {
    let commitCount = 0;
    let fileCount = 0;
    let repoCount = 0;
    const statusMap = {
      10: 'Creating',
      20: 'Translating',
      30: 'Handback',
      35: 'Handback2',
      40: 'Done'
    };
    project.repositories.forEach(repo => {
      repoCount++;
      repo.files.forEach(file => {
        fileCount++;
        commitCount += file.commits.length;
      });
    });
    return (
      <Box key={`recent_project_${project.name}_${project.stringsModified}`} align='center'>
        <Box background='white' height='flex' pad='small' width='large' round>
          <Box direction='row-responsive' justify='between' pad={{ left: 'small', right: 'small' }}>
            <Box>
              <Box direction='row-responsive' gap='small' fill>
                <Box justify='center'><Text size='large'>{project.name}</Text></Box>
              </Box>
              <Box direction='row-responsive' gap='small' align='center' justify='center' fill>
                <Box border pad={{ left: 'small', right: 'small', top: 'xsmall', bottom: 'xsmall' }} justify='center' round>
                  <Text size='small'>{`${project.stringsModified} strings`}</Text>
                </Box>
                <Box border pad={{ left: 'small', right: 'small', top: 'xsmall', bottom: 'xsmall' }} justify='center' round>
                  <Text size='small'>{`${commitCount} commits`}</Text>
                </Box>
                <Box border pad={{ left: 'small', right: 'small', top: 'xsmall', bottom: 'xsmall' }} justify='center' round>
                  <Text size='small'>{`${repoCount} repos`}</Text>
                </Box>
                <Box border pad={{ left: 'small', right: 'small', top: 'xsmall', bottom: 'xsmall' }} justify='center' round>
                  <Text size='small'>{`${fileCount} files`}</Text>
                </Box>
                <Box border pad={{ left: 'small', right: 'small', top: 'xsmall', bottom: 'xsmall' }} justify='center' round>
                  {project.awsStatus !== 'RUNNING' && project.awsStatus !== 'SUCCEEDED' ? (<Text size='small' color='red'>{`AWS Status: ${project.awsStatus}`}</Text>) : (<Text size='small' color='green'>{`AWS Status: ${project.awsStatus}`}</Text>)}
                </Box>
              </Box>
            </Box>

            <Box align='center' pad='xsmall'>
              <Stack anchor='center'>
                <Meter
                  type="circle"
                  background="light-2"
                  values={[{ value: project.statusCode, color: qlikBrand.flood.aquamarine }, { value: 40 - project.statusCode, color: 'light-2' }]}
                  size="xsmall"
                  thickness="small"
                />
                <Box direction="row" align='end' pad={{ bottom: "xsmall" }}>
                  <Text size="small">
                    {statusMap[project.statusCode]}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
        <Collapsible open={!recentProjects.open[index]}>
          <Box>
            <Button onClick={() => toggleRecentBreakdownOpen(index)} icon={<Down />} />
          </Box>
        </Collapsible>
        <Collapsible open={recentProjects.open[index]}>
          <Box align='center'>
            <Button onClick={() => toggleRecentBreakdownOpen(index)} icon={<Close color='red' />} />
            <BusBreakdown repositories={project.repositories} notes={notes} setNotes={setNotes} onNotesLayerOpen={onNotesLayerOpen} setCreatingNote={setCreatingNote} />
            <Button onClick={() => toggleRecentBreakdownOpen(index)} icon={<Close color='red' />} />
          </Box>
        </Collapsible>
      </Box>
    );
  };

  const toggleRecentBreakdownOpen = (index) => {
    let open = clone(recentProjects.open);
    open[index] = !open[index];
    setRecentProjects({ open });
  }

  const renderRecent = ({ projects: projectsList, activeProjects: active }) => {
    if (!projectsList || !projectsList.length) {
      return (<Box align='center' pad='small' gap='small' fill><Spinner /></Box>);
    }

    return (
      <Box align='center' pad='small' gap='small' fill>
        <Box direction='row' width='large' height='xxsmall' pad='medium' round>
          <Box align='start' justify='center' fill>
            <Text size='xlarge'>Recent</Text>
          </Box>
          <Box align='end' justify='center' fill>
            <Text size='xlarge'>{`Active: ${active}`}</Text>
          </Box>
        </Box>
        {projectsList.map((project, index) => {
          return renderRecentProject({ project, index });
        })}
      </Box>
    );
  };

  const renderTimer = () => {
    if (typeof nextBusCountdown !== 'string') {
      return <Spinner />;
    }
    return nextBusCountdown;
  }

  const toggleNextBusOpen = () => {
    setNextBusOpen(!nextBusOpen);
  }

  const showBusButton = (data) => {
    return Object.values(data).some(Number.isInteger);
  }

  let notesCount = Array.isArray(notes) ? notes.length : undefined;
  if (notesCount === undefined) {
    notesCount = <Spinner />
  }
  
  return (
    <Box>
      <Box>
        <Box align='center' pad={{ top: 'small', bottom: 'small' }} gap='medium'>
          <ActionableNotice description='Next Bus' notice={renderTimer()} width='large' />
          <Box direction='row-responsive' gap='medium' width='large'>
            <MetricBox description='Strings' metric={modifications.strings} />
            <MetricBox description='Words' metric={modifications.words} />
            <MetricBox description='Repositories' metric={modifications.repositories} />
            <MetricBox description='Files' metric={modifications.files} />
            <MetricBox description='Notes' metric={notesCount} onClick={()=>onNotesLayerOpen()} />
          </Box>
        </Box>
      </Box>
      <Box>
        <Box align='center'>
          <Collapsible open={!nextBusOpen}>
            <Box align='center' pad='small' gap='small' size='large' width='large'>
              {showBusButton(modifications)
                ? <Button onClick={() => toggleNextBusOpen()}><Image fit='contain' src={busImage} /></Button>
                : <Spinner />
              }
            </Box>
          </Collapsible>
          <Collapsible open={nextBusOpen}>
            <Box align='center'>
              <Button onClick={() => toggleNextBusOpen()} icon={<Close color='red' />} />
              <BusBreakdown repositories={repositories.repositories} isBusLookAhead={true} nextBusDate={nextBusDate} notes={notes} setNotes={setNotes} onNotesLayerOpen={onNotesLayerOpen} setCreatingNote={setCreatingNote}/>
              <Button onClick={() => toggleNextBusOpen()} icon={<Close color='red' />} />
            </Box>
          </Collapsible>
        </Box>

      </Box>
      <Box pad={{ top: 'small', bottom: 'large' }}>
        {renderRecent({ projects, activeProjects })}
      </Box>
      {notesLayerOpen && (
        <Notes notes={notes} onNotesLayerClose={onNotesLayerClose} nextBusDate={nextBusDate} creatingNote={creatingNote} setNotes={setNotes} />
      )}
    </Box>
  );
}

export default BusOperations;
